import { ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipShortcut,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { cn } from "@/core/utils/utils";
import { VIEWER_OVERLAY_CLASSNAME } from "./utils";

export interface SheetSearchControlProps {
  isOpen: boolean;
  focusRequestVersion: number;
  query: string;
  matchCount: number;
  activeMatchIndex: number;
  onOpen: () => void;
  onClose: () => void;
  onQueryChange: (query: string) => void;
  onPreviousMatch: () => void;
  onNextMatch: () => void;
}

const segmentButtonClassName =
  "flex shrink-0 items-center justify-center border-l border-stroke-tertiary px-3 text-tertiary-text transition-colors hover:bg-surface-secondary hover:text-secondary-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";

const inlineIconButtonClassName =
  "flex h-6 w-6 shrink-0 items-center justify-center rounded text-tertiary-text transition-colors hover:bg-surface-secondary hover:text-secondary-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:text-stroke-secondary";

// Inline find control for the spreadsheet formula bar. Collapsed it is a flat
// icon segment matching the fullscreen button; expanded it becomes a
// borderless find bar (icon, input, match count, prev/next, close) that reads
// as one more segment of the bar rather than a floating widget.
export const SheetSearchControl = ({
  isOpen,
  focusRequestVersion,
  query,
  matchCount,
  activeMatchIndex,
  onOpen,
  onClose,
  onQueryChange,
  onPreviousMatch,
  onNextMatch,
}: SheetSearchControlProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasQuery = query.trim().length > 0;
  const hasMatches = matchCount > 0;

  useEffect(() => {
    if (!isOpen) return;

    inputRef.current?.focus();
    inputRef.current?.select();
  }, [focusRequestVersion, isOpen]);

  if (!isOpen) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Search current sheet"
              onClick={onOpen}
              className={segmentButtonClassName}
            >
              <Search className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className={VIEWER_OVERLAY_CLASSNAME}>
            <TooltipShortcut keys={["mod", "F"]}>
              Search current sheet
            </TooltipShortcut>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Stack
        direction="row"
        align="center"
        gap="none"
        className="shrink-0 self-stretch border-l border-stroke-tertiary pl-3 pr-2"
      >
        <Search className="h-4 w-4 shrink-0 text-tertiary-text" />
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              onClose();
              return;
            }

            if (event.key === "Enter" && hasMatches) {
              event.preventDefault();
              if (event.shiftKey) {
                onPreviousMatch();
              } else {
                onNextMatch();
              }
            }
          }}
          type="text"
          aria-label="Search current sheet"
          placeholder="Find in sheet"
          // No focus ring, unlike the buttons beside it: this field is focused
          // programmatically the moment search opens and stays focused the whole
          // time it is on screen, so a ring would be permanently lit and would
          // tell the user nothing. The caret carries the state instead.
          className="h-full w-[min(13rem,22vw)] min-w-28 bg-transparent px-2 text-md text-primary-text outline-none placeholder:text-tertiary-text"
        />
        {hasQuery && (
          <span
            className={cn("shrink-0 whitespace-nowrap pr-1 text-sm", {
              "text-secondary-text": hasMatches,
              "text-red-50": !hasMatches,
            })}
          >
            {hasMatches
              ? `${activeMatchIndex + 1} of ${matchCount}`
              : "No matches"}
          </span>
        )}

        <Separator orientation="vertical" className="mx-1.5 h-4" />

        <Stack direction="row" align="center" gap="xs">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Previous search result"
                disabled={!hasMatches}
                onClick={onPreviousMatch}
                className={inlineIconButtonClassName}
              >
                <ChevronUp className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className={VIEWER_OVERLAY_CLASSNAME}>
              Previous result
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Next search result"
                disabled={!hasMatches}
                onClick={onNextMatch}
                className={inlineIconButtonClassName}
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className={VIEWER_OVERLAY_CLASSNAME}>
              Next result
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Close sheet search"
                onClick={onClose}
                className={inlineIconButtonClassName}
              >
                <X className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className={VIEWER_OVERLAY_CLASSNAME}>
              <TooltipShortcut keys={["Esc"]}>Close</TooltipShortcut>
            </TooltipContent>
          </Tooltip>
        </Stack>
      </Stack>
    </TooltipProvider>
  );
};
